import { memo, useState, type ComponentType, type FC } from "react";
import { ExternalLink, FileText, ImageIcon, Reply, Video } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { WhatsAppMediaType } from "src/services/WhatsAppCampaignAPI";
import { WHATSAPP_EDITOR_KEY } from "../WhatsAppCampaignConstants";
import type {
  WhatsAppMediaAttachment,
  WhatsAppPreviewMode,
  WhatsAppPreviewState,
  WhatsAppTemplate,
} from "../WhatsAppCampaignTypes";
import { MessageMeta, WhatsAppMessageText } from "./WhatsAppMessageParts";

const PREVIEW_KEY = `${WHATSAPP_EDITOR_KEY}.livePreview`;
const PREVIEW_MODES: WhatsAppPreviewMode[] = ["parameters", "example"];
const DYNAMIC_BRACKETS = "{{ }}";
const URL_BUTTON_TYPE = "URL";

const MEDIA_ICONS: Record<WhatsAppMediaType, ComponentType<{ className?: string }>> = {
  image: ImageIcon,
  video: Video,
  document: FileText,
};

interface LiveDevicePreviewProps {
  template: WhatsAppTemplate;
  attachment: WhatsAppMediaAttachment | null;
  preview: WhatsAppPreviewState;
}

const LiveDevicePreview: FC<LiveDevicePreviewProps> = ({ template, attachment, preview }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<WhatsAppPreviewMode>("parameters");
  const section = preview[mode];
  const MediaIcon = template.mediaType ? MEDIA_ICONS[template.mediaType] : null;

  const body = section?.body ?? (mode === "parameters" ? template.body : null);
  const headerText = section?.header_text ?? (mode === "parameters" ? template.headerText : null);
  const buttons =
    section?.buttons?.map(button => ({ type: button.type, text: button.text ?? "" })) ??
    (mode === "parameters" ? template.buttons : []);
  const bodyFallbackKey = preview.status === "error" ? "unavailable" : "loading";

  return (
    <aside className="wa-live-preview">
      <div className="wa-live-preview__header">
        <span className="wa-live-preview__title">
          <span className="wa-live-preview__dot" aria-hidden />
          {t(`${PREVIEW_KEY}.title`)}
        </span>
        <div className="wa-live-preview__modes" role="group" aria-label={t(`${PREVIEW_KEY}.mode`)}>
          {PREVIEW_MODES.map(previewMode => (
            <button
              key={previewMode}
              type="button"
              aria-pressed={mode === previewMode}
              className={`wa-live-preview__mode ${
                mode === previewMode ? "wa-live-preview__mode--active" : ""
              }`}
              onClick={() => setMode(previewMode)}
            >
              {t(`${PREVIEW_KEY}.modes.${previewMode}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="wa-live-preview__device">
        <div className="wa-live-preview__bubble">
          {MediaIcon && (
            <div className="wa-live-preview__media">
              {attachment?.previewUrl ? (
                <img src={attachment.previewUrl} alt="" className="wa-live-preview__image" />
              ) : (
                <MediaIcon className="wa-live-preview__placeholder" aria-hidden />
              )}
            </div>
          )}
          {headerText && (
            <WhatsAppMessageText body={headerText} className="wa-live-preview__heading" />
          )}
          {body ? (
            <WhatsAppMessageText body={body} className="wa-live-preview__text" />
          ) : (
            <p className="wa-live-preview__text" role="status">
              {t(`${PREVIEW_KEY}.${bodyFallbackKey}`)}
            </p>
          )}
          {section?.footer && (
            <p className="wa-live-preview__footer-text">{section.footer}</p>
          )}
          <MessageMeta />
        </div>
        {buttons.map((button, position) => {
          const Icon = button.type === URL_BUTTON_TYPE ? ExternalLink : Reply;
          return (
            <span key={`${button.type}-${position}`} className="wa-live-preview__button">
              <Icon className="wa-live-preview__button-icon" aria-hidden />
              {button.text}
            </span>
          );
        })}
      </div>

      {preview.errors.length > 0 && (
        <ul className="wa-live-preview__errors" role="alert">
          {preview.errors.map(error => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      )}

      <p className="wa-live-preview__caption">
        {t(`${PREVIEW_KEY}.captions.${mode}`, { brackets: DYNAMIC_BRACKETS })}
      </p>
    </aside>
  );
};

export default memo(LiveDevicePreview);
